/* eslint-disable jsx-a11y/anchor-is-valid */
import {css, Tabs, Tab, useTheme, Link} from '@mui/material';
import {
  ConfigObjectType,
  DlMuiDoctorIcon,
  DlMuiMedicalClipIcon,
  GetStylesFunction,
  LoadingSpinner,
  mixins,
  utils,
  TypeWithEtag,
  useLocalStorage,
} from '@wallaby-ui/core';
import {MerieSchema} from '@wallaby-ui/document';
import classNames from 'classnames';
import {ProviderContext, useSnackbar} from 'notistack';
import * as React from 'react';
import {Helmet} from 'react-helmet';
import {IntlShape, useIntl} from 'react-intl';

import {HospitalizationDiagnosticsTabPanel} from './HospitalizationDiagnosticsTabPanel';
import {PatientCardLayout} from './PatientCardLayout';
import {PatientDocumentsTabPanel} from './PatientDocumentsTabPanel';

interface OwnProps {
  config: ConfigObjectType;
  handleSelectedTabChange: (event: React.SyntheticEvent, selectedTabIdx: number) => void;
  reuseDocsPage: boolean;
  selectedTabIdx: number;
  riskFactorData: TypeWithEtag<MerieSchema.components['schemas']['RiskFactorsDto']>;
  oldRiskFactorIds: Set<number>;
  reloadRiskFactors: () => void;
  patientCardData: TypeWithEtag<MerieSchema.components['schemas']['PatientCardDto']>;
  isLoading: boolean;
  hospitalizationId: string;
  documentId: string;
  encounterId: number | undefined;
  reloadPatientCard: () => void;
  areFiltersOpen: {[key: string]: boolean};
  openCloseFilters: (key: 'labCharts' | 'timeline' | 'documents') => void;
  filters: {[key: string]: any};
  haveDefaultFilters: boolean;
  changeFilters: (newFilterData: any) => void;
  storageIdPrefix: string;
}

type Props = OwnProps;

/** @deprecated TODO: remove old version of component */
export const HospitalizationDetailLayout: React.FC<Props> = (props) => {
  const {
    config,
    reuseDocsPage,
    selectedTabIdx,
    handleSelectedTabChange,
    riskFactorData,
    oldRiskFactorIds,
    reloadRiskFactors,
    isLoading,
    hospitalizationId,
    documentId,
    encounterId,
    patientCardData,
    reloadPatientCard,
    areFiltersOpen,
    openCloseFilters,
    filters,
    haveDefaultFilters,
    changeFilters,
    storageIdPrefix,
  } = props;

  // TODO-JK: add state to redux
  const intl = useIntl();
  const theme = useTheme();

  const [isCommentsMaximized, _, setIsCommentsMaximized] = useLocalStorage(`${storageIdPrefix}.isCommentsMaximized`, false);

  const styles = getStyles({
    theme,
    params: {isCommentsMaximized},
  });

  const diagnosisTabLabel = utils.TranslateUtils.translate('hospitalization.detail.diagnosisTab', intl);

  const patientDocumentsTabLabel = utils.TranslateUtils.translate('hospitalization.detail.documentationTab', intl);
  const snackBar = useSnackbar();

  return (
    <div data-name={'hospitalization-detail-layout--content-wrapper'} css={styles.contentWrapper}>
      <Helmet title={utils.TranslateUtils.translate(patientCardData?.fullName, intl)} />
      <div
        css={styles.spinningContainer}
        className={classNames('dlLoadingSpinnerContainer', 'dlFlexBoxContainerInContainer', {
          dlLoadingSpinnerParent: isLoading,
        })}
      >
        <LoadingSpinner isVisible={isLoading} />
        {encounterId && patientCardData && riskFactorData && (
          <>
            <div css={styles.content}>
              <div css={styles.contentBoxWrapper}>
                <div css={styles.contentBox}>
                  <div css={styles.patientCardLayout}>
                    <PatientCardLayout
                      storageIdPrefix={storageIdPrefix}
                      data={patientCardData}
                      reloadPatientCard={reloadPatientCard}
                      config={config}
                      isCommentsMaximized={isCommentsMaximized}
                      setIsCommentsMaximized={setIsCommentsMaximized}
                    />
                  </div>
                  <div css={styles.hospitalizationDetailTabs}>
                    <Tabs css={styles.tabsWrapper} value={selectedTabIdx} onChange={handleSelectedTabChange}>
                      <Tab css={styles.tab} icon={<DlMuiDoctorIcon />} iconPosition={'start'} label={diagnosisTabLabel} />
                      <Tab css={styles.tab} icon={<DlMuiMedicalClipIcon />} iconPosition={'start'} label={patientDocumentsTabLabel} />
                      {/* TODO-JK: add other tabs */}
                    </Tabs>
                    <div css={styles.tabContentWrapper}>
                      {selectedTabIdx === 0 && (
                        <HospitalizationDiagnosticsTabPanel
                          riskFactors={riskFactorData.riskFactors}
                          riskFactorsEtag={riskFactorData._etag}
                          oldRiskFactorIds={oldRiskFactorIds}
                          reloadRiskFactors={reloadRiskFactors}
                          config={config}
                          hospitalizationId={hospitalizationId}
                          encounterId={encounterId}
                          areFiltersOpen={areFiltersOpen}
                          openCloseFilters={openCloseFilters}
                          filters={filters ?? {}}
                          changeFilters={changeFilters}
                          storageIdPrefix={storageIdPrefix}
                        />
                      )}
                      {selectedTabIdx === 1 && (
                        <PatientDocumentsTabPanel
                          config={config}
                          hospitalizationId={hospitalizationId}
                          documentId={documentId}
                          encounterId={encounterId}
                          patientCardId={patientCardData.id}
                          areFiltersOpen={areFiltersOpen}
                          openCloseFilters={openCloseFilters}
                          filters={filters}
                          haveDefaultFilters={haveDefaultFilters}
                          changeFilters={changeFilters}
                          reuseDocsPage={reuseDocsPage}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          paddingTop: '16px',
          paddingRight: '24px',
        }}
      >
        <Link
          fontSize='12px'
          style={{color: '#4D5358', cursor: 'pointer'}}
          onClick={async () => copyIdsToClipboard(patientCardData, snackBar, intl)}
        >
          {utils.TranslateUtils.translate('hospitalization.detail.copyIdValues', intl)}
          <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg' style={{marginLeft: '8px'}}>
            <g clipPath='url(#clip0_1166_12760)'>
              <path
                d='M10.5 9H6C5.17266 9 4.5 8.32734 4.5 7.5V1.5C4.5 0.672656 5.17266 0 6 0H9.28359C9.58125 0 9.86719 0.119531 10.0781 0.330469L11.6695 1.92188C11.8805 2.13281 12 2.41875 12 2.71641V7.5C12 8.32734 11.3273 9 10.5 9ZM1.5 3H3.75V4.125H1.5C1.29375 4.125 1.125 4.29375 1.125 4.5V10.5C1.125 10.7063 1.29375 10.875 1.5 10.875H6C6.20625 10.875 6.375 10.7063 6.375 10.5V9.75H7.5V10.5C7.5 11.3273 6.82734 12 6 12H1.5C0.672656 12 0 11.3273 0 10.5V4.5C0 3.67266 0.672656 3 1.5 3Z'
                fill='#4D5358'
              />
            </g>
            <defs>
              <clipPath id='clip0_1166_12760'>
                <rect width='12' height='12' fill='white' />
              </clipPath>
            </defs>
          </svg>
        </Link>
      </div>
    </div>
  );
};

async function copyIdsToClipboard(
  patientCardData: TypeWithEtag<MerieSchema.components['schemas']['PatientCardDto']>,
  snackBar: ProviderContext,
  intl: IntlShape
) {
  // REFACTOR: use copyToClipboard() instead
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(
      JSON.stringify({
        encounterId: patientCardData.encounterId,
        encounterSrcId: patientCardData.encounterSrcId,
        patientId: patientCardData.patientId,
        patientSrcId: patientCardData.patientSrcId,
      })
    );
    snackBar.enqueueSnackbar(utils.TranslateUtils.translate('hospitalization.detail.copyIdValuesSnack', intl));
  } else {
    snackBar.enqueueSnackbar(utils.TranslateUtils.translate('hospitalization.detail.copyIdValuesErrorSnack', intl), {variant: 'error'});
  }
}

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    ${mixins.contentWrapper};
    overflow: hidden;
    padding: ${options?.theme?.spacing(3, 4, 3, 0)};
  `,
  content: css`
    ${mixins.content};
    overflow: hidden;
    gap: ${options?.theme?.spacing(4)};
  `,
  contentBoxWrapper: css`
    ${mixins.contentBoxWrapper};
    height: 100%;
    overflow: hidden;
    gap: ${options?.theme?.spacing(3)};
  `,
  contentBox: css`
    ${mixins.contentBox};
    height: 100%;
    overflow: hidden;
    flex-direction: row;
    gap: ${options?.theme?.spacing(4)};
    border-radius: ${options?.theme?.borderRadius};
  `,
  patientCardLayout: css`
    min-width: 260px;
    width: ${options?.params?.isCommentsMaximized ? '460px' : '260px'};
    height: 100%;
  `,
  hospitalizationDetailTabs: css`
    width: 100%;
    height: 100%;
    display: flex;
    overflow: hidden;
    gap: ${options?.theme?.spacing(3)};
    flex-direction: column;
  `,
  tabsWrapper: css`
    width: 100%;
    position: relative;
    overflow: hidden;
    padding-left: ${options?.theme?.spacing(4)};
    height: 62px;
    align-items: center;
    flex-shrink: 0;
  `,
  tabContentWrapper: css`
    overflow: auto;
    height: 100%;
    padding-right: 5px;
  `,
  tab: css`
    background-color: #ffffff;
  `,
  spinningContainer: css`
    width: 100%;
    height: 100%;
  `,
});
